import React from "react";
import { makeStyles } from "@material-ui/core";
import MarkdownRenderer from "./MarkdownRenderer";
import MetricCards from "./MetricCards";
import RiskFlags from "./RiskFlags";
import DataTable from "./DataTable";
import ChartRenderer from "./ChartRenderer";
import FollowUpButtons from "./FollowUpButtons";
import FeedbackBar from "./FeedbackBar";

var useStyles = makeStyles(function () {
  return {
    "@global @keyframes fadeInUp": {
      from: { opacity: 0, transform: "translateY(6px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
    userLabel: {
      fontSize: "12.5px",
      fontWeight: 600,
      color: "#111827",
      letterSpacing: "0.3px",
      marginBottom: "5px",
      fontFamily: "'DM Sans', sans-serif",
      textTransform: "uppercase",
    },
    userContent: {
      fontSize: "14.5px",
      lineHeight: 1.65,
      color: "#374151",
      fontFamily: "'IBM Plex Sans', sans-serif",
    },
    assistantLabelRow: {
      display: "flex",
      alignItems: "center",
      marginBottom: "8px",
    },
    nqIcon: {
      width: "22px",
      height: "22px",
      borderRadius: "6px",
      background: "linear-gradient(135deg, #1e3a5f, #2563eb)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginRight: "8px",
      flexShrink: 0,
    },
    nqIconText: {
      color: "#fff",
      fontSize: "9px",
      fontWeight: 700,
      lineHeight: 1,
      fontFamily: "'DM Sans', sans-serif",
    },
    assistantName: {
      fontSize: "12.5px",
      fontWeight: 600,
      color: "#1e3a5f",
      fontFamily: "'DM Sans', sans-serif",
    },
    timestamp: {
      marginLeft: "auto",
      fontSize: "11px",
      color: "#B0B5BF",
      fontFamily: "'IBM Plex Sans', sans-serif",
    },
    errorCard: {
      background: "#fef2f2",
      border: "1px solid #fecaca",
      borderRadius: "8px",
      padding: "12px 16px",
      color: "#991b1b",
      fontSize: "14px",
      lineHeight: 1.6,
    },
    followUpWrap: {
      marginTop: "14px",
    },
  };
});

function formatTimestamp(createdAt) {
  if (!createdAt) return "";
  var date = new Date(createdAt);
  if (isNaN(date.getTime())) return "";
  var now = new Date();
  var diffMs = now - date;
  var diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) return "Just now";

  var today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  var msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (msgDay.getTime() === today.getTime()) {
    return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
  }

  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
    ", " +
    date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

function splitContent(text) {
  if (!text) return { summary: "", body: "" };
  // Split at the first bold heading pattern like **Key Findings:** or **Standouts:** etc.
  var headingMatch = text.match(/\n\s*\*\*[A-Z][^*]{2,50}\*\*\s*:?\s*\n/);
  if (headingMatch) {
    var idx = text.indexOf(headingMatch[0]);
    return {
      summary: text.slice(0, idx).trim(),
      body: text.slice(idx).trim(),
    };
  }
  // Also try markdown heading ## or ###
  var mdHeadingMatch = text.match(/\n\s*#{2,3}\s+[A-Z][^\n]+\n/);
  if (mdHeadingMatch) {
    var idx2 = text.indexOf(mdHeadingMatch[0]);
    return {
      summary: text.slice(0, idx2).trim(),
      body: text.slice(idx2).trim(),
    };
  }
  // No heading found — treat everything as summary
  return { summary: text, body: "" };
}

// Primary renderer: uses answer_blocks from the v3.1 API
//
// Rendering order:
//   1. Intro block       — sets context for the reader
//   2. MetricCards       — headline numbers (meaningful after intro)
//   3. Section blocks    — analysis, with table/chart placed at viz_context
//   4. RiskFlags         — warnings before the wrap-up
//   5. Closing block     — final summary/outlook
//   6. FollowUpButtons   — rendered by MessageItem, outside this function
//
function renderBlocksContent(message, isNew) {
  var blocks = message.answer_blocks;
  var metrics = message.key_metrics;
  var flags = message.risk_flags;
  var table = message.table;
  var chart = message.chart;
  var mode = message.display_mode;

  var delay = 0;
  var anim = function () {
    if (!isNew) return undefined;
    var d = delay;
    delay += 60;
    return { animation: "fadeInUp 200ms ease-out " + d + "ms both" };
  };

  var hasTables = Array.isArray(message.tables) && message.tables.length > 0;
  var hasMetrics = metrics && metrics.length > 0;
  var hasFlags = flags && flags.length > 0;
  var hasTable = !!table && !hasTables;
  var hasChart = !!chart;

  var elements = [];
  var metricsInserted = false;

  // Render each block in order
  for (var i = 0; i < blocks.length; i++) {
    var block = blocks[i];
    if (!block || !block.content) continue;

    // Insert KPI cards after the intro block (so context comes first)
    if (block.type !== "intro" && hasMetrics && !metricsInserted) {
      metricsInserted = true;
      elements.push(
        <div key="metrics" style={Object.assign({}, anim(), { marginTop: "16px" })}>
          <MetricCards metrics={metrics} />
        </div>
      );
      // Insert risk flags immediately below metrics
      if (hasFlags) {
        elements.push(<div key="flags" style={Object.assign({}, anim(), { marginTop: "16px" })}><RiskFlags flags={flags} /></div>);
        hasFlags = false;
      }
    }

    if (block.type === "viz_context") {
      // Render viz_context as subtle caption, then table/chart immediately after
      elements.push(
        <div key={"block-" + i} style={Object.assign({}, anim(), {
          fontSize: "13.5px",
          fontStyle: "italic",
          color: "#6B7280",
          fontFamily: "'IBM Plex Sans', sans-serif",
          margin: "12px 0 8px 0",
        })}>
          {block.content}
        </div>
      );
      if (hasChart && (!mode || mode === "chart_only" || mode === "table_and_chart")) {
        elements.push(<div key="chart" style={anim()}><ChartRenderer chart={chart} /></div>);
      }
      if (hasTables && (!mode || mode === "table_only" || mode === "table_and_chart")) {
        message.tables.forEach(function (t, idx) {
          elements.push(<div key={"tables-" + idx} style={anim()}><DataTable table={t} /></div>);
        });
      } else if (hasTable && (!mode || mode === "table_only" || mode === "table_and_chart")) {
        elements.push(<div key="table" style={anim()}><DataTable table={table} /></div>);
      }
    } else if (block.type === "closing") {
      // Insert risk flags before closing
      if (hasFlags) {
        elements.push(<div key="flags" style={Object.assign({}, anim(), { marginTop: "16px" })}><RiskFlags flags={flags} /></div>);
        hasFlags = false; // prevent duplicate
      }

      var closingStyle = Object.assign({}, anim() || {}, { color: "#4B5563", marginTop: "12px" });
      elements.push(
        <div key={"block-" + i} style={closingStyle}>
          <MarkdownRenderer content={block.content} followUps={null} stripTable={false} />
        </div>
      );
    } else if (block.type === "section" && block.heading) {
      // Section with heading: render heading and content as separate elements
      // so CSS controls the exact gap (no <br/> from markdown \n)
      var sectionStyle = anim() || {};
      sectionStyle = Object.assign({}, sectionStyle, { marginTop: "16px" });

      elements.push(
        <div key={"block-" + i} style={sectionStyle}>
          <div style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: "15.5px",
            fontWeight: 600,
            color: "#1E3A5F",
            lineHeight: 1.35,
            marginBottom: "4px",
          }}>
            {block.heading}
          </div>
          <MarkdownRenderer content={block.content} followUps={null} stripTable={false} />
        </div>
      );
    } else {
      // intro, section without heading — rendered via MarkdownRenderer
      var blockStyle = anim() || {};
      if (block.type === "section") {
        blockStyle = Object.assign({}, blockStyle, { marginTop: "16px" });
      }

      elements.push(
        <div key={"block-" + i} style={blockStyle}>
          <MarkdownRenderer content={block.content} followUps={null} stripTable={false} />
        </div>
      );
    }
  }

  // If metrics weren't inserted (e.g. no intro block, or only conversational blocks)
  if (hasMetrics && !metricsInserted) {
    elements.unshift(
      <div key="metrics" style={isNew ? { animation: "fadeInUp 200ms ease-out 0ms both" } : undefined}>
        <MetricCards metrics={metrics} />
      </div>
    );
  }

  // If table/chart exist but no viz_context block was present, append them
  var hasVizContext = blocks.some(function (b) { return b && b.type === "viz_context"; });
  if (!hasVizContext) {
    if (hasChart) {
      elements.push(<div key="chart-fallback" style={anim()}><ChartRenderer chart={chart} /></div>);
    }
    if (hasTables) {
      message.tables.forEach(function (t, idx) {
        elements.push(<div key={"tables-fallback-" + idx} style={anim()}><DataTable table={t} /></div>);
      });
    } else if (hasTable) {
      elements.push(<div key="table-fallback" style={anim()}><DataTable table={table} /></div>);
    }
  }

  // If risk flags weren't inserted (e.g. no closing block), append them
  if (hasFlags) {
    elements.push(<div key="flags" style={anim()}><RiskFlags flags={flags} /></div>);
  }

  return <>{elements}</>;
}

// Legacy renderer: uses flat answer string + splitContent() regex
// Kept as fallback for old messages that don't have answer_blocks
function renderLegacyContent(message, isNew) {
  var mode = message.display_mode;
  var content = message.content;
  var metrics = message.key_metrics;
  var flags = message.risk_flags;
  var table = message.table;
  var chart = message.chart;
  var followUps = message.follow_ups;
  var hasStripTable = !!table;

  var anim = function (delayMs) {
    if (!isNew) return undefined;
    return { animation: "fadeInUp 200ms ease-out " + delayMs + "ms both" };
  };

  var parts = splitContent(content);

  if (parts.summary && table && table.caption) {
    var captionText = table.caption.trim();
    if (captionText) {
      var escaped = captionText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      var captionRegex = new RegExp("\\*{0,2}\\s*" + escaped + "\\s*\\*{0,2}", "g");
      parts.summary = parts.summary.replace(captionRegex, "");
      parts.summary = parts.summary.replace(/\n{3,}/g, "\n\n").trim();
    }
  }

  var hasSummary = parts.summary && parts.summary.length > 0;
  var hasBody = parts.body && parts.body.length > 0;
  var hasTables = Array.isArray(message.tables) && message.tables.length > 0;
  var hasTable = !!table && !hasTables;
  var hasChart = !!chart;
  var hasMetrics = metrics && metrics.length > 0;
  var hasFlags = flags && flags.length > 0;

  if (mode === "table_only" || mode === "chart_only" || mode === "table_and_chart" || ((hasTable || hasTables || hasChart) && !mode)) {
    return (
      <>
        {hasSummary && (
          <div style={Object.assign({}, anim(0), { marginBottom: "4px" })}>
            <MarkdownRenderer content={parts.summary} followUps={followUps} stripTable={hasStripTable} />
          </div>
        )}
        {hasMetrics && <div style={anim(80)}><MetricCards metrics={metrics} /></div>}
        {hasFlags && <div style={anim(160)}><RiskFlags flags={flags} /></div>}
        {hasChart && <div style={anim(240)}><ChartRenderer chart={chart} /></div>}
        {hasTables && message.tables.map(function (t, idx) {
          return <div key={"tables-" + idx} style={anim(320 + idx * 80)}><DataTable table={t} /></div>;
        })}
        {hasTable && <div style={anim(320)}><DataTable table={table} /></div>}
        {hasBody && (
          <div style={Object.assign({}, anim(400), { marginTop: "20px" })}>
            <MarkdownRenderer content={parts.body} followUps={followUps} stripTable={hasStripTable} />
          </div>
        )}
      </>
    );
  }

  if (mode === "kpi_only") {
    return (
      <>
        {hasMetrics && <div style={anim(0)}><MetricCards metrics={metrics} /></div>}
        <div style={anim(80)}>
          <MarkdownRenderer content={content} followUps={followUps} stripTable={hasStripTable} />
        </div>
        {hasFlags && <div style={anim(320)}><RiskFlags flags={flags} /></div>}
      </>
    );
  }

  return (
    <>
      <div style={anim(0)}>
        <MarkdownRenderer content={content} followUps={followUps} stripTable={hasStripTable} />
      </div>
      {hasMetrics && <div style={anim(80)}><MetricCards metrics={metrics} /></div>}
      {hasFlags && <div style={anim(320)}><RiskFlags flags={flags} /></div>}
    </>
  );
}

function renderAssistantContent(message, isNew) {
  // Use answer_blocks when available (v3.1 API — primary path)
  if (message.answer_blocks && message.answer_blocks.length > 0) {
    return renderBlocksContent(message, isNew);
  }
  // Fallback to legacy flat-string rendering
  return renderLegacyContent(message, isNew);
}

var MessageItem = React.memo(function MessageItem({ message, isLast, isNew, sendingMessage, onFollowUpSelect, userName }) {
  var classes = useStyles();

  if (!message) return null;

  // User message
  if (message.role === "user") {
    return (
      <div>
        <div className={classes.userLabel}>{userName}</div>
        <div className={classes.userContent}>{message.content}</div>
      </div>
    );
  }

  // Error message
  if (message.isError) {
    return (
      <div>
        <div className={classes.assistantLabelRow}>
          <div className={classes.nqIcon}>
            <span className={classes.nqIconText}>NQ</span>
          </div>
          <span className={classes.assistantName}>NQ Assist</span>
          <span className={classes.timestamp}>{formatTimestamp(message.created_at)}</span>
        </div>
        <div className={classes.errorCard}>{message.content}</div>
      </div>
    );
  }

  // Assistant message
  return (
    <div>
      <div className={classes.assistantLabelRow}>
        <div className={classes.nqIcon}>
          <span className={classes.nqIconText}>NQ</span>
        </div>
        <span className={classes.assistantName}>NQ Assist</span>
        <span className={classes.timestamp}>{formatTimestamp(message.created_at)}</span>
      </div>
      {renderAssistantContent(message, isNew)}
      {isLast && !sendingMessage && message.follow_ups && message.follow_ups.length > 0 && (
        <div className={classes.followUpWrap} style={isNew ? { animation: "fadeInUp 200ms ease-out 480ms both" } : undefined}>
          <FollowUpButtons followUps={message.follow_ups} onSelect={onFollowUpSelect} />
        </div>
      )}
      {message.id && !message.isError && (
        <FeedbackBar messageId={message.id} feedback={message.feedback} />
      )}
    </div>
  );
});

export default MessageItem;
